import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import ReactHtmlParser from 'react-html-parser';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';
import _some from 'lodash/some';
import { getCardSettings } from 'cards/util/cardSettingsUtils';
import { CARD_TYPES } from 'cards/constants/CardTypes';

// Components
import { Carousel } from '@zola/zola-ui';
import ConnectedListingDetail from '../../CardListing/ListingDetail/ListingDetail';

// Actions
import { getRelatedCardsFromSuite, unMountRelatedCards } from '../../../actions/cardCatalogActions';
import { trackCardListEvent, trackCardProductClicked } from '../../../actions/tracking';

// PDP Actions
import { setPDPInitialVariationColor } from '../../../reducers/pdp/actions';

// Selectors
import { getRelatedCardsFromCurrentSuite } from '../../../selectors/cardCatalogSelector';
import { getCardType } from '../../../reducers/pdp/selectors';

// Stylesheets
import './RelatedCardsCarousel.less';

export class RelatedCardsCarousel extends Component {
  componentDidMount() {
    const {
      family,
      cardType,
      onGetRelatedCardsFromSuite,
      unMountRelatedCardsFromSuite,
      fallbackDisplay,
    } = this.props;
    unMountRelatedCardsFromSuite();
    onGetRelatedCardsFromSuite(family, null, cardType).then(data => {
      if (fallbackDisplay && _isEmpty(data.payload.relatedCards.suites)) {
        fallbackDisplay();
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { relatedCards } = this.props;
    if (relatedCards.length !== prevProps.relatedCards.length) {
      this.trackProductsViewed();
    }
  }

  componentWillUnmount() {
    const { unMountRelatedCardsFromSuite } = this.props;
    unMountRelatedCardsFromSuite();
  }

  /**
   * TODO: Fixes needed for this - it should only include products on screen.
   * It should emit another event when the user scrolls the next (or previous) and shows more products.
   */
  trackProductsViewed() {
    const { relatedCards, trackListViewed } = this.props;

    const trackedProducts = _reduce(
      relatedCards,
      (accumulator, card, idx) => {
        const {
          suiteUUID,
          uuid,
          suiteName,
          min_price_cents: minPriceCents,
          type,
          colors,
          name,
          key,
        } = card;
        const { urlSlug } = getCardSettings(type);

        // Hack to prevent sending too many products in the "Product List Viewed" event.
        const numSlidesDesktop = 3;
        if (idx + 1 > numSlidesDesktop) return accumulator;

        if (!_some(accumulator, { uuid })) {
          const { plp_front_image_url: imageUrl } = colors[0];
          accumulator.push({
            product_id: suiteUUID,
            sku_id: uuid,
            category: type,
            name,
            brand: suiteName,
            variant: key,
            price: (minPriceCents / 100).toFixed(2),
            quantity: 1,
            position: idx,
            url: `/wedding-planning/${urlSlug}/${card.suiteUUID}`,
            image_url: imageUrl || 'N/A',
          });
        }
        return accumulator;
      },
      []
    );

    const metaData = {
      location: 'PDP',
      section: 'RELATED_CARDS_CAROUSEL',
      products: trackedProducts,
    };

    trackListViewed({
      name: 'Product List Viewed',
      properties: metaData,
    });
  }

  handleTrackingProductClickedEvent(meta) {
    const { handleTrackCardProductClicked } = this.props;
    const { name, properties } = meta;

    handleTrackCardProductClicked({
      name,
      properties,
    });
  }

  render() {
    const {
      relatedCards,
      lightView,
      isAdminView,
      onSetInitialVariationColor,
      initialVariationColor,
      filterColor,
      family,
      referenceType,
      cardType,
    } = this.props;
    const basePlanningUrl = isAdminView ? '/wedding-planning/admin' : '/wedding-planning';
    const cardTypes = {
      SAVE_THE_DATE: {
        text: 'Save The Dates',
        path: `${basePlanningUrl}/save-the-date/shop`,
      },
      CHANGE_THE_DATE: {
        text: 'Change The Dates',
        path: `${basePlanningUrl}/change-the-date/shop`,
      },
      INVITATION: {
        text: 'Invitations',
        path: `${basePlanningUrl}/invitations/shop`,
      },
      THANK_YOU: {
        text: 'Thank You Cards',
        path: `${basePlanningUrl}/thank-you-cards/shop`,
      },
      MENU: {
        text: 'Menus',
        path: `${basePlanningUrl}/menus/shop`,
      },
      PROGRAM: {
        text: 'Programs',
        path: `${basePlanningUrl}/programs/shop`,
      },
      PLACE_CARDS: {
        text: 'Place Cards',
        path: `${basePlanningUrl}/place-cards/shop`,
      },
      HOLIDAY: {
        text: 'Holiday Cards',
        path: `${basePlanningUrl}/holiday-cards/shop`,
      },
      DETAILS: {
        text: 'Enclosures Cards',
        path: `${basePlanningUrl}/enclosures/shop`,
      },
    };
    const gridCarouselSettings = {
      dots: false,
      arrows: true,
      infinite: relatedCards.length > 3,
      swipeToSlide: true,
      slidesToShow: 3,
      slidesToScroll: 3,
      lazyLoad: false,
      useCSS: true,
      useTransform: false,
      adaptiveHeight: false,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    };

    const carouselSuiteData =
      relatedCards.length > 0 &&
      relatedCards.map(card => (
        <div className="related-cards-carousel__wrapper" key={card.uuid}>
          <ConnectedListingDetail
            card={card}
            filterColor={filterColor}
            basePlanningUrl={basePlanningUrl}
            onSetInitialVariationColor={onSetInitialVariationColor}
            initialVariationColor={initialVariationColor}
            lightView={lightView}
            handleTrackCardProductClicked={meta => this.handleTrackingProductClickedEvent(meta)}
          />
          {!lightView && (
            <Link
              to={cardTypes[card.type].path}
              className="related-cards-carousel__more-link zola-ui link link-arrow"
            >
              More {cardTypes[card.type].text}
            </Link>
          )}
        </div>
      ));

    let title = family
      ? {
          invites: `Shop The Full <span class="related-cards-carousel__title--emphasis">${family}</span> Suite`,
          pdp: `<span class="related-cards-carousel__title--ligther">Shop The Full ${family} Suite</span>`,
          website: `Pair It With Paper Products In <span class="related-cards-carousel__title--emphasis">${family}</span>`,
        }
      : {
          invites: `Shop Most Popular Designs`,
          pdp: `<span class="related-cards-carousel__title--ligther">Shop Most Popular Designs</span>`,
          website: `Pair It With Paper Products`,
        };

    if (cardType === CARD_TYPES.ctd) {
      title = {
        invites: `Shop More Change The Dates`,
        pdp: `<span class="related-cards-carousel__title--ligther">Shop More Change The Dates</span>`,
        website: `Pair It With Paper Products`,
      };
    }

    return (
      <div>
        {carouselSuiteData && (
          <div className="related-cards-carousel__section__inner-wrapper">
            <h3 className="related-cards-carousel__title">
              {ReactHtmlParser(title[referenceType])}
            </h3>
            <Carousel
              content={carouselSuiteData}
              settings={gridCarouselSettings}
              className="related-cards-carousel__content"
            />
          </div>
        )}
      </div>
    );
  }
}

RelatedCardsCarousel.propTypes = {
  unMountRelatedCardsFromSuite: PropTypes.func,
  relatedCards: PropTypes.arrayOf(PropTypes.shape({})),
  onGetRelatedCardsFromSuite: PropTypes.func,
  onSetInitialVariationColor: PropTypes.func,
  initialVariationColor: PropTypes.string,
  family: PropTypes.string,
  lightView: PropTypes.bool,
  isAdminView: PropTypes.bool,
  filterColor: PropTypes.string,
  referenceType: PropTypes.string,
  fallbackDisplay: PropTypes.func,
  trackListViewed: PropTypes.func,
  handleTrackCardProductClicked: PropTypes.func,
};

const mapStateToProps = state => ({
  relatedCards: getRelatedCardsFromCurrentSuite(state),
  cardType: getCardType(state),
});

const mapDispatchToProps = dispatch => ({
  onGetRelatedCardsFromSuite: (familyName, initialColor, cardType) =>
    dispatch(getRelatedCardsFromSuite(familyName, initialColor, cardType)),
  unMountRelatedCardsFromSuite: () => dispatch(unMountRelatedCards()),
  onSetInitialVariationColor: color => dispatch(setPDPInitialVariationColor(color)),
  trackListViewed: meta => dispatch(trackCardListEvent(meta)),
  handleTrackCardProductClicked: meta => dispatch(trackCardProductClicked(meta)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RelatedCardsCarousel);



// WEBPACK FOOTER //
// ./src/cards/components/common/RelatedCardsCarousel/RelatedCardsCarousel.jsx